import { useState, useEffect, useRef, useCallback } from "react";
import { knowledgeApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Download, Paperclip, File, FileText, X, ZoomIn, Trash2 } from "lucide-react";
import { formatFileSize, isImageFile, isPdfFile } from "@/lib/fileHelpers";

// Self-contained attachments manager for a knowledge-base article.
export function ArticleAttachments({ articleId }) {
  const toast = useToast();
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const loadingPreviewsRef = useRef(new Set());

  const fetchAttachments = useCallback(async () => {
    try {
      const res = await knowledgeApi.getAttachments(articleId);
      setAttachments(res.data);
    } catch (err) {
      setAttachments([]);
    }
  }, [articleId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(0);
    let uploaded = 0;
    const total = files.length;
    for (const file of files) {
      try {
        await knowledgeApi.uploadAttachment(articleId, file);
        uploaded++;
        setUploadProgress(Math.round((uploaded / total) * 100));
      } catch (error) {
        toast.error(`Fehler bei ${file.name}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} ${uploaded === 1 ? 'Datei' : 'Dateien'} hochgeladen`);
      fetchAttachments();
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handleUploadAttachment = async (e) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;
    await uploadFiles([file]);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) await uploadFiles(files);
  };

  const handleDownloadAttachment = async (att) => {
    try {
      const response = await knowledgeApi.downloadAttachment(att.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", att.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Download fehlgeschlagen");
    }
  };

  const handleDeleteAttachment = async (att) => {
    try {
      await knowledgeApi.deleteAttachment(att.id);
      toast.success("Anhang gelöscht");
      fetchAttachments();
    } catch (error) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const loadImagePreview = useCallback(async (att) => {
    try {
      const response = await knowledgeApi.downloadAttachment(att.id);
      const blob = new Blob([response.data], { type: att.content_type });
      const url = URL.createObjectURL(blob);
      setAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, previewUrl: url } : a)));
    } catch (error) {
      // Silent error - preview shows placeholder
    } finally {
      loadingPreviewsRef.current.delete(att.id);
    }
  }, []);

  const openImagePreview = async (att) => {
    if (!att.previewUrl) {
      const response = await knowledgeApi.downloadAttachment(att.id);
      const blob = new Blob([response.data], { type: att.content_type });
      const url = URL.createObjectURL(blob);
      setPreviewImage({ ...att, fullUrl: url });
    } else {
      setPreviewImage({ ...att, fullUrl: att.previewUrl });
    }
  };

  useEffect(() => {
    attachments.forEach((att) => {
      if (isImageFile(att.original_filename) && !att.previewUrl && !loadingPreviewsRef.current.has(att.id)) {
        loadingPreviewsRef.current.add(att.id);
        loadImagePreview(att);
      }
    });
  }, [attachments, loadImagePreview]);

  return (
    <>
      <Card
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`transition-all duration-200 ${isDragging ? 'ring-2 ring-primary ring-offset-2 bg-primary/5' : ''}`}
        data-testid="attachments-dropzone"
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Anhänge
            </CardTitle>
            <label className="cursor-pointer">
              <input type="file" className="hidden" onChange={handleUploadAttachment} disabled={uploading} multiple />
              <Button variant="outline" size="sm" disabled={uploading} asChild>
                <span>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Hochladen
                </span>
              </Button>
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {uploading && uploadProgress > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-muted-foreground">Wird hochgeladen...</span>
                <span className="font-medium">{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {isDragging && (
            <div className="border-2 border-dashed border-primary rounded-lg p-8 mb-4 text-center bg-primary/5 animate-pulse">
              <Upload className="w-10 h-10 mx-auto text-primary mb-2" />
              <p className="text-primary font-medium">Dateien hier ablegen</p>
              <p className="text-sm text-muted-foreground mt-1">Mehrere Dateien werden unterstützt</p>
            </div>
          )}

          {attachments.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {attachments.map((att) => (
                <div
                  key={att.id}
                  className="group relative rounded-lg border bg-card overflow-hidden hover:shadow-md transition-all"
                  data-testid={`attachment-${att.id}`}
                >
                  {isImageFile(att.original_filename) ? (
                    <div
                      className="aspect-video bg-muted flex items-center justify-center cursor-pointer relative overflow-hidden"
                      onClick={(e) => { e.stopPropagation(); openImagePreview(att); }}
                      data-testid={`image-preview-${att.id}`}
                    >
                      {att.previewUrl ? (
                        <>
                          <img src={att.previewUrl} alt={att.original_filename} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center pointer-events-none">
                            <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Laden...</span>
                        </div>
                      )}
                    </div>
                  ) : isPdfFile(att.original_filename) ? (
                    <div className="aspect-video bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950/20 dark:to-red-900/20 flex items-center justify-center">
                      <div className="text-center">
                        <FileText className="w-12 h-12 text-red-500 mx-auto mb-1" />
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">PDF</span>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video bg-muted/50 flex items-center justify-center">
                      <File className="w-12 h-12 text-muted-foreground/50" />
                    </div>
                  )}

                  <div className="p-3">
                    <p className="font-medium text-sm truncate" title={att.original_filename}>
                      {att.original_filename}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(att.size)}</p>

                    <div className="flex gap-1 mt-2">
                      <Button variant="outline" size="sm" className="flex-1 h-8" onClick={() => handleDownloadAttachment(att)}>
                        <Download className="w-3 h-3 mr-1" />
                        Herunterladen
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteAttachment(att)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : !isDragging && (
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
              <Upload className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">Keine Anhänge</p>
              <p className="text-xs text-muted-foreground mt-1">Dateien hierher ziehen oder Button klicken</p>
            </div>
          )}
        </CardContent>
      </Card>

      {previewImage && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <Button
              variant="ghost"
              size="icon"
              className="absolute -top-12 right-0 text-white hover:bg-white/20"
              onClick={() => setPreviewImage(null)}
            >
              <X className="w-6 h-6" />
            </Button>
            <img
              src={previewImage.fullUrl}
              alt={previewImage.original_filename}
              className="max-w-full max-h-[85vh] mx-auto rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center mt-3 text-sm">
              {previewImage.original_filename} ({formatFileSize(previewImage.size)})
            </p>
          </div>
        </div>
      )}
    </>
  );
}
